import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { format } from "date-fns";
import { useDispatch } from "react-redux";

import { Typography } from "@mui/material";

import TopHeader from "components/common/TopHeader";
import PageLoader from "components/common/PageLoader";
import { AlertType } from "components/common/Snackbar";

import { getPublicHolidays, getHolidayImage, getCalendarLinks } from "services/holidays";
import { usePermissions } from "components/Hook";
import { showSnackbar } from "store/actions/Utility";

import HolidayFilters from "components/Screens/HolidaySchedule/components/HolidayFilters/HolidayFilters";
import HolidayMonthGroup from "components/Screens/HolidaySchedule/components/HolidayMonthGroup/HolidayMonthGroup";
import HolidayDetailDrawer from "components/Screens/HolidaySchedule/components/HolidayDetailDrawer/HolidayDetailDrawer";
import AddToCalendarDialog from "components/Screens/HolidaySchedule/components/AddToCalendarDialog/AddToCalendarDialog";

import {
  groupHolidaysByMonth,
  getCountryOptions,
  getNextUpcomingDateKey,
} from "components/Screens/HolidaySchedule/utils";

import "./HolidaySchedule.scss";

function HolidaySchedule() {
  const { loggedInUser } = usePermissions();
  const dispatch = useDispatch();

  const [allHolidays, setAllHolidays] = useState({ data: [], countryList: [] });
  const [loadingHolidaysList, setLoadingHolidaysList] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(loggedInUser?.type === "employee" ? "" : "All");
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCalendarDialogOpen, setIsCalendarDialogOpen] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [loadingCalendarLinks, setLoadingCalendarLinks] = useState(false);

  const today = useMemo(() => new Date(), []);
  const currentMonthRef = useRef(null);
  const hasScrolledRef = useRef(false);

  const [loadedYears, setLoadedYears] = useState(new Set([new Date().getFullYear()]));
  const [isLoadingNextYear, setIsLoadingNextYear] = useState(false);
  const lastMonthRef = useRef(null);

  useEffect(() => {
    const fetchHolidays = async () => {
      const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
      if (loggedInUser?.type === "customer" && !customerKey) return;

      setLoadingHolidaysList(true);
      setLoadedYears(new Set([new Date().getFullYear()]));

      try {
        let region = "";
        if (loggedInUser?.type === "customer") {
          region = selectedCountry !== "All" ? selectedCountry : "";
        }

        const response = await getPublicHolidays(customerKey, region);

        if (response?.holidays) {
          const transformedHolidays = Object.values(response.holidays)
            .flatMap((datesArray) => datesArray)
            .flatMap((dateObj) => dateObj.holidays);

          const regions = response.employeeRegions || [
            ...new Set(transformedHolidays.map((h) => h.region).filter(Boolean)),
          ];

          setAllHolidays({
            data: transformedHolidays,
            countryList: regions,
          });
        }
      } catch (error) {
        setAllHolidays({ data: [], countryList: [] });
      } finally {
        setLoadingHolidaysList(false);
      }
    };

    fetchHolidays();
  }, [selectedCountry, loggedInUser?.customerId, loggedInUser?.pendingCustomerId, loggedInUser?.type]);

  const fetchNextYearHolidays = useCallback(async () => {
    if (loadedYears.size >= 2) {
      return;
    }

    if (isLoadingNextYear) {
      return;
    }

    const customerKey = loggedInUser?.customerId || loggedInUser?.pendingCustomerId;
    if (loggedInUser?.type === "customer" && !customerKey) {
      return;
    }

    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;

    if (loadedYears.has(nextYear)) {
      return;
    }

    setIsLoadingNextYear(true);

    try {
      let region = "";
      if (loggedInUser?.type === "customer") {
        region = selectedCountry !== "All" ? selectedCountry : "";
      }

      const response = await getPublicHolidays(customerKey, region, nextYear);

      if (response?.holidays) {
        const allTransformedHolidays = Object.values(response.holidays)
          .flatMap((datesArray) => datesArray)
          .flatMap((dateObj) => dateObj.holidays);

        const nextYearHolidays = allTransformedHolidays.filter((h) => {
          const year = new Date(h.startDate).getFullYear();
          return year === nextYear;
        });

        if (nextYearHolidays.length > 0) {
          setAllHolidays((prev) => ({
            ...prev,
            data: [...prev.data, ...nextYearHolidays],
          }));

          setLoadedYears((prev) => new Set([...prev, nextYear]));
        } else {
          setLoadedYears((prev) => new Set([...prev, nextYear]));
        }
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: "Failed to load more holidays. Please try again.",
          dispatch,
        })
      );
    } finally {
      setIsLoadingNextYear(false);
    }
  }, [loadedYears, isLoadingNextYear, loggedInUser, selectedCountry, dispatch]);

  useEffect(() => {
    if (loadingHolidaysList || isLoadingNextYear) {
      return undefined;
    }

    if (loadedYears.size >= 2) {
      return undefined;
    }

    if (!lastMonthRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          fetchNextYearHolidays();
        }
      },
      {
        threshold: 0.5,
        rootMargin: "0px",
      }
    );

    const currentRef = lastMonthRef.current;
    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loadedYears.size, loadingHolidaysList, isLoadingNextYear, fetchNextYearHolidays]);

  useEffect(() => {
    if (!loadingHolidaysList && currentMonthRef.current && allHolidays.data.length > 0 && !hasScrolledRef.current) {
      currentMonthRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      hasScrolledRef.current = true;
    }
  }, [loadingHolidaysList, allHolidays.data.length]);

  const handleCountryChange = useCallback((value) => {
    setSelectedCountry(value);
  }, []);

  const handleThisMonthClick = useCallback(() => {
    if (currentMonthRef.current) {
      currentMonthRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleAddToCalendarClick = useCallback(async () => {
    if (calendarData?.length) {
      setIsCalendarDialogOpen(true);
      return;
    }

    setLoadingCalendarLinks(true);
    try {
      const response = await getCalendarLinks(loggedInUser?.customerId || loggedInUser?.pendingCustomerId);
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        setCalendarData(response.data);
        setIsCalendarDialogOpen(true);
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: "No calendar links available.",
            dispatch,
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: error?.message || "Failed to fetch calendar links. Please try again.",
          dispatch,
        })
      );
    } finally {
      setLoadingCalendarLinks(false);
    }
  }, [calendarData.length, loggedInUser?.customerId, loggedInUser?.pendingCustomerId, dispatch]);

  const handleCloseCalendarDialog = useCallback(() => {
    setIsCalendarDialogOpen(false);
  }, []);

  const handleHolidayClick = useCallback(
    async (holiday) => {
      let holidayToDisplay = holiday;

      if (holiday.dayNumber) {
        const linkedHolidays = allHolidays.data.filter((h) => {
          if (!h.dayNumber) {
            return false;
          }

          if (holiday.id && h.id) {
            return h.id === holiday.id;
          }

          if (holiday.poolId && h.poolId) {
            return h.poolId === holiday.poolId;
          }

          return h.name === holiday.name && h.region === holiday.region;
        });

        if (linkedHolidays.length > 1) {
          const sortedHolidays = [...linkedHolidays].sort((a, b) => (a.dayNumber || 0) - (b.dayNumber || 0));

          const firstHoliday = sortedHolidays[0];
          const lastHoliday = sortedHolidays[sortedHolidays.length - 1];

          const derivedTotalDays = Math.max(
            firstHoliday.totalDays || 0,
            lastHoliday.totalDays || 0,
            lastHoliday.dayNumber || 0,
            sortedHolidays.length
          );

          let aggregatedEndDate = lastHoliday.endDate || firstHoliday.endDate || firstHoliday.startDate;

          if (!aggregatedEndDate && derivedTotalDays > 1) {
            const startDateObj = new Date(firstHoliday.startDate);
            startDateObj.setDate(startDateObj.getDate() + (derivedTotalDays - 1));
            aggregatedEndDate = format(startDateObj, "yyyy-MM-dd");
          }

          holidayToDisplay = {
            ...holiday,
            isMultiDay: true,
            startDate: firstHoliday.startDate,
            endDate: aggregatedEndDate,
            totalDays: derivedTotalDays || sortedHolidays.length,
          };
        }
      }

      // Open drawer first with holiday data
      setSelectedHoliday(holidayToDisplay);
      setIsDrawerOpen(true);

      if (holidayToDisplay.poolId) {
        const imageUrl = await getHolidayImage(holidayToDisplay.poolId, true);
        if (imageUrl) {
          setSelectedHoliday((prev) => ({
            ...prev,
            image: imageUrl,
          }));
        }
      }
    },
    [allHolidays.data]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedHoliday(null);
  }, []);

  const groupedHolidays = useMemo(() => groupHolidaysByMonth(allHolidays), [allHolidays]);

  const nextUpcomingDateKey = useMemo(() => getNextUpcomingDateKey(allHolidays?.data, today), [allHolidays, today]);

  const countryOptions = useMemo(
    () =>
      getCountryOptions(allHolidays).map((option) => ({
        ...option,
        leftSlot: (
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: option.color,
            }}
          />
        ),
      })),
    [allHolidays]
  );

  return (
    <>
      <Helmet>
        <title>Edge | Holiday Schedule</title>
      </Helmet>
      {loadingHolidaysList && <PageLoader />}

      <div className="holiday-schedule-container">
        <TopHeader title="Holiday schedule" backIcon="/main/dashboard" />

        <div className="holiday-schedule-content">
          <HolidayFilters
            selectedCountry={selectedCountry}
            countryOptions={countryOptions}
            onCountryChange={handleCountryChange}
            onThisMonthClick={handleThisMonthClick}
            onAddToCalendarClick={handleAddToCalendarClick}
            isLoadingCalendar={loadingCalendarLinks}
            userType={loggedInUser?.type}
          />

          <div className="holiday-schedule-list">
            {Object.keys(groupedHolidays).length > 0
              ? Object.entries(groupedHolidays).map(([monthYear, holidays], index, array) => {
                  const isCurrentMonth = monthYear === format(today, "MMMM, yyyy");

                  const isLastMonth = index === array.length - 1 && loadedYears.size < 2;

                  let assignedRef = null;

                  if (isCurrentMonth && isLastMonth) {
                    assignedRef = (el) => {
                      currentMonthRef.current = el;
                      lastMonthRef.current = el;
                    };
                  } else if (isCurrentMonth) {
                    assignedRef = currentMonthRef;
                  } else if (isLastMonth) {
                    assignedRef = lastMonthRef;
                  }

                  return (
                    <HolidayMonthGroup
                      key={monthYear}
                      monthYear={monthYear}
                      holidays={holidays}
                      today={today}
                      onHolidayClick={handleHolidayClick}
                      monthRef={assignedRef}
                      upcomingDateKey={nextUpcomingDateKey}
                    />
                  );
                })
              : !loadingHolidaysList && (
                  <div className="no-holidays">
                    <Typography>No holidays found</Typography>
                  </div>
                )}
          </div>
        </div>

        <HolidayDetailDrawer open={isDrawerOpen} onClose={handleCloseDrawer} holiday={selectedHoliday} />
        <AddToCalendarDialog
          open={isCalendarDialogOpen}
          onClose={handleCloseCalendarDialog}
          calendarData={calendarData}
        />
      </div>
    </>
  );
}

export default HolidaySchedule;
